import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { CameraGeolocation, Framerate, ImageQuality, ResponseTime, TimelineConsistency } from '../../../services/central-api/third-party-approved-companies';
import { translate } from '../../../services/translate';
import { UseFormReturn } from 'react-hook-form';
import FormDropDownPicker from '../../../components/FormDropDownPicker';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FormInput from '../../../components/FormInput';
import { WindowInformation } from '../../../services/window-information';
import { SectionHeader } from '../../FormCommons';

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
}

export default function VideoAnalysisForm({ form }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const responseTimeItems = useMemo(() => {
        return Object.values(ResponseTime)
            .map((val) => ({
                label: translate(val),
                value: val,
            }));
    }, []);

    const imageQualityItems = useMemo(() => {
        return Object.values(ImageQuality)
            .map((val) => ({
                label: translate(val),
                value: val,
            }));
    }, []);

    const framerateItems = useMemo(() => {
        return Object.values(Framerate)
            .map((val) => ({
                label: translate(val),
                value: val,
            }));
    }, []);

    const timelineConsistencyItems = useMemo(() => {
        return Object.values(TimelineConsistency)
            .map((val) => ({
                label: translate(val),
                value: val,
            }));
    }, []);

    const cameraGeolocationItems = useMemo(() => {
        return Object.values(CameraGeolocation)
            .map((val) => ({
                label: translate(val),
                value: val,
            }));
    }, []);

    return (<ScrollView contentContainerStyle={[{ flexDirection: 'column', padding: 16, rowGap: 24, backgroundColor: theme.colors.white['500'] }]}>
        <SectionHeader title='responseTime' viewZIndex={4}>
            <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                <FormDropDownPicker
                    label={translate('responseTimeLive')}
                    items={responseTimeItems}
                    zIndex={1}
                    fieldName={'responseTimeLive'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 2 }]}
                    dropDownDirection='BOTTOM'
                />
                <FormDropDownPicker
                    label={translate('responseTimeTimeline')}
                    items={responseTimeItems}
                    zIndex={1}
                    fieldName={'responseTimeTimeline'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 1 }]}
                    dropDownDirection='BOTTOM'
                />
            </View>
        </SectionHeader>

        <SectionHeader title='imageQuality' viewZIndex={3}>
            <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                <FormDropDownPicker
                    label={translate('imageQualityLive')}
                    items={imageQualityItems}
                    zIndex={1}
                    fieldName={'imageQualityLive'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 2 }]}
                    dropDownDirection='BOTTOM'
                />
                <FormDropDownPicker
                    label={translate('imageQualityTimeline')}
                    items={imageQualityItems}
                    zIndex={1}
                    fieldName={'imageQualityTimeline'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 1 }]}
                    dropDownDirection='BOTTOM'
                />
            </View>
        </SectionHeader>

        <SectionHeader title='framerate' viewZIndex={2}>
            <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                <FormDropDownPicker
                    label={translate('framerateLive')}
                    items={framerateItems}
                    zIndex={1}
                    fieldName={'framerateLive'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 2 }]}
                    dropDownDirection='BOTTOM'
                />
                <FormDropDownPicker
                    label={translate('framerateTimeline')}
                    items={framerateItems}
                    zIndex={1}
                    fieldName={'framerateTimeline'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 1 }]}
                    dropDownDirection='BOTTOM'
                />
            </View>
        </SectionHeader>

        <SectionHeader title='others' viewZIndex={1}>
            <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                <FormDropDownPicker
                    label={translate('timelineConsistency')}
                    items={timelineConsistencyItems}
                    zIndex={1}
                    fieldName={'timelineConsistency'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 2 }]}
                    dropDownDirection='BOTTOM'
                />
                <FormInput
                    form={form}
                    fieldName='resolution'
                    label={translate('resolution')}
                    placeholder={translate('resolution')}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={styles.itemView(1)}
                />
                <FormDropDownPicker
                    label={translate('cameraGeolocation')}
                    items={cameraGeolocationItems}
                    zIndex={1}
                    fieldName={'cameraGeolocation'}
                    form={form}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[styles.itemView(1), { zIndex: 1 }]}
                    dropDownDirection='BOTTOM'
                />
            </View>
        </SectionHeader>
    </ScrollView>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    sectionDisplay: (isSmallView: boolean) => ({
        gap: 16,
        flexDirection: isSmallView ? 'column' : 'row',
    }),
    itemView: (flex: number) => ({
        gap: 8,
        flex
    })
}));
